import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import {useParams, useHistory, useLocation} from "react-router-dom";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import CreatableSelect from "react-select/creatable";
import useAxios from "../../utils/useAxios";
import TournamentGroups from "./groups/TournamentGroups";
import TournamentGrids from "./draws/TournamentGrids";
import TournamentDrawStages from "./draws/TournamentDrawStages";
import TournamentPlayers from "./players/TournamentPlayers";
import TournamentSchedule from "./schedule/TournamentSchedule";
import TournamentRatingTab from "./rating/TournamentRatingTab";
import TournamentInformationForm from "./info/TournamentInformationForm";
import TournamentResults from "./TournamentResults";
import { tournamentCache, tournamentSubscribers } from "../../utils/tournamentCache";
import { refreshSubscribers } from "../../utils/tournamentRefreshBus";
import { tournamentSearchOptionsSubscribers } from "../../utils/tournamentSearchOptionsBus";
import TournamentHeader, { getVisibleTabs } from "./TournamentHeader";
import TournamentHero from "./TournamentHero";
import TournamentPreviewFrame from "./TournamentPreviewFrame";
import { PreviewHiddenProvider } from "../../contexts/PreviewHiddenContext";
import TournamentSwipeTabs from "../../components/TournamentSwipeTabs";
import { Helmet } from "react-helmet-async";
import { isPreviewFrame } from "../../utils/previewFrame";
import useTournamentHeroCollapse from "../../hooks/useTournamentHeroCollapse";
import AuthContext from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import useAppLanguage from "../../hooks/useAppLanguage";
import getTournamentHeaderStickyScrollY from "../../utils/getTournamentHeaderStickyScrollY";
import { reassertScrollTo } from "../../utils/reassertScroll";
import { takePendingAuthSwitchScroll } from "../../utils/pendingAuthSwitchScroll";

const Tournament = ({ is_authorized = true }) => {
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const api = useAxios();
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const { theme, setTheme, accent, setAccent, compactTable, setCompactTable, previewWidth } = useTheme();
    const { language, handleLanguageChange } = useAppLanguage();
    // Always the tournaments list, never history.goBack() - this used to
    // try to retrace how the visitor actually arrived (goBack() when a real
    // in-app PUSH got them here, straight to the list otherwise), but a
    // single "back" control with two different destinations depending on
    // arrival history read as unpredictable. Lives here (not
    // TournamentHeader.js) so both that component's own back arrow AND
    // TournamentHero.js's duplicate copy (its own hero-top row, same as
    // club/player's) agree on the same target.
    const goUp = (e) => {
        e.preventDefault();
        history.push(is_authorized ? "/admin/tournaments" : "/tournaments");
    };

    // "Screen size" preview (Header.js's own +/- stepper, Public View only) -
    // swaps out ONLY the tab content below for a scaled same-origin iframe
    // at the chosen width (see TournamentPreviewFrame.js's own comment for
    // why that needs a real separate browsing context rather than plain
    // CSS). Hero/TournamentHeader above are NOT part of this swap - they're
    // the real page's own chrome (same bucket as the site Header/BottomNav,
    // which were never touched by this to begin with), always shown at the
    // visitor's actual device width regardless of what's being previewed
    // below them. !isPreviewFrame() guards against this component's OWN
    // nested copy (rendered a second time inside the preview iframe itself,
    // same route) ever trying to open a preview of its own - that copy's
    // previewWidth already stays 0 in practice (nothing restores it from
    // localStorage inside the iframe, see Header.js's own restore effect),
    // but being explicit here doesn't rely on that staying true.
    const showPreviewFrame = previewWidth > 0 && !is_authorized && !isPreviewFrame();
    // Refs mutated during render, not useState+useEffect - see App.js's own
    // past version of this exact pattern (git history) for why an
    // effect-based "remember this happened" one render late enough.
    // Keeps TournamentPreviewFrame mounted (toggled by CSS display below)
    // once previewWidth has ever gone positive, instead of unmounting/
    // remounting it (full iframe reload) on every subsequent toggle back to
    // Mobile/off.
    const hasUsedPreviewRef = useRef(previewWidth > 0);
    if (previewWidth > 0) hasUsedPreviewRef.current = true;
    const hasUsedPreview = hasUsedPreviewRef.current;
    const lastPreviewWidthRef = useRef(previewWidth || 768);
    if (previewWidth > 0) lastPreviewWidthRef.current = previewWidth;
    const lastPreviewWidth = lastPreviewWidthRef.current;
    // One search box for the whole tournament page, replacing what used to
    // be a separate search icon/input duplicated in each tab (Участники/
    // Жеребкування/Розклад/Рейтинг) - lives here, not in any tab, because
    // renderTabContent() below fully unmounts/remounts the active tab on
    // every ?tab= switch (see its own key={refreshKey} comment), so state
    // held inside a tab can't survive switching away and back. The actual
    // icon that toggles this lives in TournamentHeader.js, rendered inline
    // below (same tree now), so this is just plain state/props - no bus.
    //
    // Chips, not a plain string - same CreatableSelect-with-chips look and
    // behavior as Розклад's own search (ScheduleTable.js), instead of the
    // plain text input this used to be. Chip *options* (player names, group
    // letters, bracket places) come from whichever tab is actually mounted -
    // it has the data, this component doesn't - published up through
    // tournamentSearchOptionsBus, the same cross-tree pattern already used
    // for the open/close toggle above.
    const [searchOpen, setSearchOpen] = useState(false);
    // TournamentHeader.js's own .tournament-header-wrap is `position:
    // sticky` now (lives right here in this tree, after TournamentHero),
    // not a fixed header measured globally via a CSS var - headerHeight is
    // a plain local ResizeObserver reading of it (see headerWrapCallbackRef
    // below) used only to position this search row right under it.
    // Snapshotting it into searchTopPx once, right as the row opens (rather
    // than reading headerHeight live the whole time it's open), keeps the
    // row still if the header's own height happens to change while the
    // search box is already open, instead of visibly sliding to chase it.
    const [headerHeight, setHeaderHeight] = useState(0);
    const headerResizeObserverRef = useRef(null);
    const headerWrapCallbackRef = useCallback((el) => {
        if (headerResizeObserverRef.current) {
            headerResizeObserverRef.current.disconnect();
            headerResizeObserverRef.current = null;
        }
        if (!el) return;
        const update = () => setHeaderHeight(el.offsetHeight);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        headerResizeObserverRef.current = ro;
    }, []);
    const [searchTopPx, setSearchTopPx] = useState(null);
    const [searchChips, setSearchChips] = useState([]);
    // Purely for the input's own controlled display/typing (and react-
    // select's own default option filtering) - NOT fed into matching.
    // Highlighting/dim/scroll only ever reacts to committed chips
    // (searchChips itself), same as Розклад - a per-keystroke version of
    // this was tried and reverted (felt chaotic mid-typing, and briefly
    // wiped results when interacting with an existing chip).
    const [searchInputValue, setSearchInputValue] = useState("");
    const [searchOptions, setSearchOptions] = useState([]);
    const toggleSearch = useCallback(() => {
        setSearchOpen(prev => {
            const next = !prev;
            if (next) {
                setSearchTopPx(headerHeight ? `${headerHeight}px` : null);
            } else {
                setSearchChips([]);
                setSearchInputValue("");
                setSearchTopPx(null);
            }
            return next;
        });
    }, [headerHeight]);
    useEffect(() => {
        const handler = (options) => setSearchOptions(options || []);
        tournamentSearchOptionsSubscribers.add(handler);
        return () => tournamentSearchOptionsSubscribers.delete(handler);
    }, []);
    // A cached tournament fetched in public view has can_edit:false baked in.
    // Trusting it as-is when mounting into admin mode would broadcast that
    // stale, wrong-permission object to subscribers (e.g. Header's bottom
    // nav) before the authorized refetch below resolves - flashing the
    // public/user nav for a moment. Only seed from cache when it actually
    // matches the mode we're mounting into.
    const cachedMatchesMode = tournamentCache[id] && (!is_authorized || tournamentCache[id].can_edit);
    const [tournament, setTournament] = useState(cachedMatchesMode ? tournamentCache[id] : null);
    const [loading, setLoading] = useState(!cachedMatchesMode);
    // Falls back to whichever tab this same tournament was last showing
    // (sessionStorage, see the persist effect below) when the URL doesn't
    // say - a fresh `goToTournament` from the list never carries `?tab=`,
    // so without this, leaving from e.g. Жеребкування and reopening the
    // same tournament always landed back on Інформація. An explicit
    // `?tab=` (a tab switch within the page, a shared link) always wins.
    const getRememberedTab = () => {
        try {
            return sessionStorage.getItem(`tournamentLastTab_${id}`) || "information";
        } catch {
            return "information";
        }
    };
    // Lazy initializer (not a hardcoded "information" corrected a tick later
    // by the effect below) - reading the real ?tab= param straight from the
    // URL on the very first render is what stops every single mount of this
    // component (a hard page reload, admin/public toggle, any time this
    // gets remounted) from unconditionally flashing Information first and
    // only then jumping to whatever tab the URL actually said, regardless
    // of mode.
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get("tab") || getRememberedTab());
    const [hasGuestToken, setHasGuestToken] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    // Telegram-style hero (see TournamentHero.js) - same ClubProfileModal/
    // PlayerProfileModal behavior: TournamentHeader (rendered inline below,
    // right after the hero) crossfades its crest+title in via `progress` as
    // this scrolls past.
    const { heroSentinelRef, progress: heroProgress } = useTournamentHeroCollapse(true);
    // A previous tab's options (e.g. Жеребкування's group/bracket chips)
    // must not linger once its own tab unmounts - the newly-mounted tab
    // re-publishes its own set right away, but only once its own data has
    // loaded, so without this reset there'd be a window (and, for tabs that
    // never publish anything, forever) where a stale, no-longer-relevant set
    // of options is still offered.
    useEffect(() => {
        setSearchOptions([]);
    }, [activeTab]);
    // Guards fetchTournament against out-of-order responses: the mount
    // effect fires one, and every settings save/reset (via
    // requestTournamentRefresh) fires another - without this, a slower
    // pre-change response can land after the newer one and silently
    // overwrite the freshly-changed tournament with the old one.
    const tournamentReqRef = useRef(0);

    // Switching tabs used to visibly snap the header back open: the newly
    // mounted tab's content (often a loading skeleton, briefly) is shorter
    // than whatever was already scrolled past, so the browser auto-clamps
    // window.scrollY down the instant that DOM swap commits - which
    // un-collapses the hero/header for a beat even though the user never
    // scrolled up themselves. Same fix as ClubProfileModal.js's own
    // captureScrollBeforeSwitch/reassert pair: capture the real scroll
    // position right before the switch and keep re-asserting it for a short
    // window right after, instead of letting the browser's own clamp win.
    // Centralized here (not wired into every tab Link's own onClick like
    // ClubProfileModal.js does) because every way of switching tabs - the
    // header's own Link row, TournamentSwipeTabs' swipe gesture, the
    // Export modal's hidden iframe - all funnel through this same
    // location.search change first.
    const preSwitchScrollRef = useRef(null);
    // REVERTED: a clampToContentTop helper lived here briefly, capping
    // `target` to the new tab's own measured content-top minus the header's
    // height and a gap. Real-browser testing (headless Chrome against this
    // same dev server) showed it was landing BELOW the sticky engagement
    // point (getTournamentHeaderStickyScrollY, used below) on essentially
    // every switch, not just genuinely short ones - the header's identity
    // row (opacity: heroProgress) settled at a different PARTIAL value per
    // destination tab (0.6 on one, 0.77 on another, 0.85 on a third)
    // instead of reaching 1 (fully collapsed) - because there's no real
    // extra gap between the header's natural bottom and the content's
    // natural top to subtract in the first place (confirmed by sampling
    // scrollY/opacity every 40ms across several switches - each settled
    // instantly to one stable value, never oscillating, just the WRONG
    // value). That read as the header never fully snapping shut, and
    // differently so per tab, i.e. exactly "the header jumps around" again.
    // Reverted rather than re-tuned - the actual short-content-overlap bug
    // this was meant to fix needs to be re-diagnosed from a clean baseline
    // (now possible: this session set up headless Chrome via playwright-
    // core against the system's installed Chrome, see scratchpad/*.js) in
    // whatever real-tournament case reproduces it, not patched blind again.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlTab = params.get("tab");
        const tab = urlTab || getRememberedTab();
        // Folds the remembered tab into the URL too, not just this
        // component's own activeTab state - TournamentHeader.js reads the
        // active tab straight off location.search for its own underline/
        // highlight (it has no access to this component's state), so
        // without this it kept pointing at Інформація while the content
        // below had already landed on the restored tab. Only fires once
        // (the next run already has urlTab set, so this is skipped) and
        // only when there's an actual non-default tab to restore - a bare
        // "information" default doesn't need spelling out in the URL.
        if (!urlTab && tab !== "information") {
            const next = new URLSearchParams(location.search);
            next.set("tab", tab);
            history.replace({ search: next.toString() });
        }
        if (tab !== activeTab) {
            const rawScrollY = window.scrollY;
            // Clamped to the header's own sticky engagement point, not
            // carried over verbatim - past that point scrollY is into the
            // OLD tab's own content, which means nothing on a different tab.
            // Every tab can always hold at least this point (see the
            // min-height on the tab-content wrapper below), so this is
            // always safe to reassert post-switch.
            //
            // Was computed off heroSentinelRef's position instead (a 1px div
            // right after the hero's own content), then off
            // headerWrapEl.offsetTop directly - both undershot on at least
            // some tabs (by the hero's own bottom margin, and then by a
            // margin-collapse-dependent amount between .page_container and
            // its children that isn't a fixed constant). getTournamentHeader-
            // StickyScrollY sidesteps both by reading the header's real
            // flow position directly instead of inferring it - see that
            // util's own comment.
            preSwitchScrollRef.current = Math.min(rawScrollY, getTournamentHeaderStickyScrollY());
        }
        setActiveTab(tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Writes back whatever getRememberedTab() above reads - the actual
    // "remember the tab" half of this feature. sessionStorage (not
    // localStorage): this is a this-session cache of where you left off,
    // not a standing preference that should survive into a new browser
    // session or follow onto another tournament.
    useEffect(() => {
        try {
            sessionStorage.setItem(`tournamentLastTab_${id}`, activeTab);
        } catch {}
    }, [id, activeTab]);

    const tabSwitchMountedRef = useRef(false);
    useLayoutEffect(() => {
        // Skip the initial mount - nothing to restore yet, and the hero
        // hasn't necessarily measured its sentinel at this point either.
        if (!tabSwitchMountedRef.current) {
            tabSwitchMountedRef.current = true;
            return;
        }
        const target = preSwitchScrollRef.current;
        if (target == null) return;
        preSwitchScrollRef.current = null;
        // A fixed-duration window (originally ~1.5s, matching
        // ClubProfileModal.js's own reassert loop) turned out not to be a
        // fight against one single post-switch layout change, but against
        // every participant avatar in the new tab loading in one at a time
        // over the real network (the dev server proxies /media/ straight
        // through to production - see setupProxy.js) - each completed image
        // is its own reflow, and on a cold image cache that kept still going well past
        // 1.5s, so the window closed while the browser's own scroll-anchor
        // was still actively dragging window.scrollY up to compensate,
        // reading as the header continuing to "climb" back open after the
        // fix had already given up. Tracking consecutive already-correct
        // frames instead of a fixed tick count means a fast/cached tab (the
        // common case) still stops almost immediately, while a slow one
        // keeps getting corrected for as long as something is actually still
        // fighting it - capped so a tab that never settles can't pin forever.
        // Extracted to reassertScroll.js - the Admin/Public View switch
        // below needs the exact same loop.
        //
        // NOTE: a since-reverted attempt tried releasing the min-height
        // spacer below once the loop looked "stable" (nothing fighting
        // window.scrollY for ~500ms), so a tab that's genuinely shorter than
        // `target` would fall back to its own real height instead of
        // staying pinned forever. That broke the thing this loop exists
        // for: images loading in BELOW the current scroll position don't
        // move window.scrollY at all (no scroll-anchor compensation
        // needed), so the loop reported "stable" and released the spacer
        // WHILE those images were still loading - clamping scroll down
        // early (a visible jump) even on tabs that would have ended up tall
        // enough once everything finished loading. Permanent min-height
        // (below) avoids that; the genuinely-short-tab case this was meant
        // to fix needs a different signal than scroll stability (e.g. an
        // explicit per-tab "content settled" callback) - not attempted here.
        // A later clampToContentTop attempt measured the new tab's own
        // content-top instead and got reverted too - see preSwitchScrollRef's
        // own comment above for why.
        return reassertScrollTo(target);
    }, [activeTab]);

    // Admin/Public View switch (Header.js's own mode-switch link) jumps
    // the page the same way a tab switch used to before the fix above -
    // but it can't reuse that fix's own capture step: is_authorized is a
    // plain prop flowing straight down from App.js's URL-derived value,
    // not buffered through a piece of local state the way the URL's own
    // ?tab= param is buffered through activeTab above. By the time ANY
    // effect in this component runs in response to is_authorized changing,
    // React has already committed (tab-switch case: the effect that would
    // capture scrollY runs BEFORE activeTab itself has been updated, i.e.
    // while the OLD tab's content is still what's on screen) the NEW
    // is_authorized layout - the old content (editable admin fields
    // appearing/disappearing, say) is already gone, so there's nothing
    // left here to measure the "before" scroll position against. Capturing
    // has to happen synchronously in Header.js's own click handler, while
    // the old layout is still genuinely on screen - see
    // pendingAuthSwitchScroll.js for that handoff.
    const isAuthorizedMountedRef = useRef(false);
    useLayoutEffect(() => {
        if (!isAuthorizedMountedRef.current) {
            isAuthorizedMountedRef.current = true;
            return;
        }
        const target = takePendingAuthSwitchScroll();
        if (target == null) return;
        return reassertScrollTo(target);
    }, [is_authorized]);

    const fetchTournament = (forced = false) => {
        const thisReq = ++tournamentReqRef.current;
        // Don't show full-page loading if we already have tournament data (e.g. background refresh)
        if (!tournament || forced) {
            setLoading(true);
        }
        api.get(`/api/v1.0/tournaments/${id}/`)
            .then(res => {
                if (thisReq !== tournamentReqRef.current) return;
                tournamentCache[id] = res.data;
                setTournament(res.data);
                tournamentSubscribers.forEach(cb => cb(id, res.data));
            })
            .catch(error => {
                if (thisReq !== tournamentReqRef.current) return;
                console.error(error);
            })
            .finally(() => {
                if (thisReq === tournamentReqRef.current) {
                    setLoading(false);
                }
            });
    };

    // Refetch whenever the tournament ID changes, or whenever a settings
    // modal reports a change / reset via requestTournamentRefresh
    useEffect(() => {
        fetchTournament();
    }, [id, refreshKey]);

    useEffect(() => {
        const refreshHandler = (targetId) => {
            if (!targetId || targetId === id) {
                setRefreshKey(k => k + 1);
            }
        };
        refreshSubscribers.add(refreshHandler);
        return () => {
            refreshSubscribers.delete(refreshHandler);
        };
    }, [id]);

    useEffect(() => {
        const token = localStorage.getItem("guest_access_token");
        if (token) {
            setHasGuestToken(true);
        }
    }, []);

    // Plain setTournament only updates this page's own local state -
    // Header.js (still mounted globally, for the Settings/Rating modals and
    // bottom nav it owns) keeps a separate copy fed by tournamentSubscribers,
    // which otherwise only fires on the initial fetch above. Without also
    // syncing the cache/subscribers here, an in-place edit (e.g. renaming in
    // the Information tab) updated this page but left those stale.
    const updateTournament = (updater) => {
        setTournament(prev => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            if (next) {
                tournamentCache[id] = next;
                tournamentSubscribers.forEach(cb => cb(id, next));
            }
            return next;
        });
    };

    const renderTabContent = () => {
        const isSuperAdmin = !!user?.is_superuser;
        // Before the tournament fetch resolves (or when a stale cache from a
        // different mode was deliberately not seeded - see cachedMatchesMode
        // above), tournament?.can_edit/can_write_in_base are simply unknown,
        // not false - but treating "unknown" as false made every admin-mode
        // navigation without a warm matching cache render the public/
        // read-only tab first and then jump to the editable one once the
        // fetch landed. `is_authorized` (route + login, already resolved
        // synchronously - see App.js) is the correct optimistic guess for
        // that brief window: right for the overwhelmingly common case (an
        // admin opening their own tournament), and self-corrects the moment
        // real data arrives on the rare case it guessed wrong (a shared
        // admin link to a tournament this user doesn't actually own).
        const canWrite = (loading ? is_authorized : tournament?.can_write_in_base) || isSuperAdmin;
        const canEdit = ((loading ? is_authorized : tournament?.can_edit) && !hasGuestToken) || isSuperAdmin;

        const props = {
            tournament,
            setTournament: updateTournament,
            loading,
            is_authorized: is_authorized && canEdit,
            can_write_in_base: canWrite,
            refreshKey,
            searchChips
        };

        // key={refreshKey}: each tab's own stage list (useFetchStages) only
        // ever fetches once, on its own mount - refreshKey being passed down
        // as a plain prop did nothing to make Groups/Grids/Schedule notice a
        // settings change (a newly added stage, a reset, etc.) short of a
        // full page reload. Forcing a remount here is what actually gets
        // them to refetch, matching what useFetchStages' own comment already
        // assumed was happening.
        switch (activeTab) {
            case "information": return <TournamentInformationForm key={refreshKey} {...props} />;
            // Kept for TournamentExportModal.js's hidden-iframe PDF/PNG export,
            // which still loads ?tab=groups / ?tab=brackets standalone to
            // screenshot #groups-block / #brackets-block one at a time - not
            // reachable from the header nav anymore (see "draw-stages" below).
            case "groups": return <TournamentGroups key={refreshKey} {...props} />;
            case "brackets":
            case "draws": return <TournamentGrids key={refreshKey} {...props} />;
            case "draw-stages": return <TournamentDrawStages key={refreshKey} {...props} />;
            case "results": return <TournamentResults key={refreshKey} {...props} />;
            case "players": return <TournamentPlayers key={refreshKey} {...props} />;
            case "schedule": return <TournamentSchedule key={refreshKey} {...props} />;
            // TournamentRatingTab folds both rating "views" behind one
            // sub-tab bar: очки турніру (TournamentRating, this tournament's
            // own results - still also reachable as its own sub-tab inside
            // "players" above) and the full/overall standings (RatingTable,
            // same component the admin's standalone /admin/ratings/:id page
            // and the rating-badge modal use).
            case "rating": return <TournamentRatingTab key={refreshKey} {...props} />;
            default: return <div>Tab not found</div>;
        }
    };

    const visibleTabs = useMemo(() => getVisibleTabs(tournament, is_authorized), [tournament, is_authorized]);

    const ogImage = tournament?.cover_url || tournament?.logo || tournament?.image || "";

    // .page_container's own padding-bottom (index.css) is what clears the
    // fixed bottom nav everywhere else - it's keyed off `.tournament-bottom-
    // nav` existing as a sibling, which only happens in the actual outer
    // page: that nav is rendered by Header.js, and Header.js is deliberately
    // never mounted at all inside the "Screen size" preview iframe (see
    // App.js's own isPreviewFrame check), so that selector never matches
    // there and this content would otherwise butt
    // flush against the framed viewport's own bottom edge - overlapping the
    // OUTER page's real, still-fixed bottom nav sitting visually on top of
    // it (Жеребкування) or leaving nothing to smooth the size jump when a
    // short tab (Розклад) ends well above it. Matching the same 64px here
    // for the framed copy keeps every tab's own bottom clearance identical
    // to what a real, non-previewed visit already gets.
    const previewFramePadding = isPreviewFrame() ? { paddingBottom: "64px" } : null;

    return (
        <Container className="page_container" style={{ marginTop: "-2px", ...previewFramePadding }}>
            <Helmet>
                {tournament?.name && <meta property="og:title" content={tournament.name} />}
                <meta property="og:description" content={tournament?.name ? `Приєднуйтесь до турніру ${tournament.name}!` : "Приєднуйтесь до турніру!"} />
                {ogImage && <meta property="og:image" content={ogImage} />}
            </Helmet>
            {/* Skipped inside the preview iframe, same reasoning (and same
                isPreviewFrame() gate) as TournamentHeader right below - the
                "Screen size" preview is meant to show CONTENT only at the
                chosen width (see TournamentPreviewFrame.js's own opening
                comment), not re-layout the identity/avatar/name row too.
                That row isn't simulated-width content so much as the real
                page's own chrome, same bucket as the real site Header/
                BottomNav, which already stay at the visitor's real device
                width outside the iframe entirely. */}
            {!isPreviewFrame() && (
                <TournamentHero
                    tournament={tournament}
                    heroSentinelRef={heroSentinelRef}
                    goUp={goUp}
                    user={user}
                    tournamentId={id}
                    isAuthorized={is_authorized}
                    onToggleSearch={toggleSearch}
                    theme={theme} setTheme={setTheme}
                    accent={accent} setAccent={setAccent}
                    compactTable={compactTable} setCompactTable={setCompactTable}
                    language={language} handleLanguageChange={handleLanguageChange}
                />
            )}
            {!isPreviewFrame() && (
                <TournamentHeader
                    tournament={tournament}
                    tournamentId={id}
                    isAuthorized={is_authorized}
                    wrapRef={headerWrapCallbackRef}
                    heroProgress={heroProgress}
                    goUp={goUp}
                    onToggleSearch={toggleSearch}
                    user={user}
                    theme={theme} setTheme={setTheme}
                    accent={accent} setAccent={setAccent}
                    compactTable={compactTable} setCompactTable={setCompactTable}
                    language={language} handleLanguageChange={handleLanguageChange}
                />
            )}
            {searchOpen && (
                <ul
                    className="tabs"
                    style={{
                        marginTop: "8px",
                        marginBottom: "5px",
                        flexWrap: "wrap",
                        // Pins right under the tournament header - searchTopPx
                        // is a snapshot of the header's own real measured
                        // height (see headerWrapCallbackRef above), not a
                        // guessed constant.
                        position: "sticky",
                        top: searchTopPx ?? 0,
                        zIndex: 14,
                        background: "var(--body-background)"
                    }}
                >
                    <li style={{ flex: "1 1 200px" }}>
                        {/* Same chip-based CreatableSelect look/behavior as
                            Розклад's own search (ScheduleTable.js) - options
                            come from whichever tab is mounted (see
                            tournamentSearchOptionsBus), so a plain typed
                            query still works as free text everywhere, while
                            Участники/Рейтинг additionally offer player-name
                            chips and Жеребкування also offers group/bracket
                            chips. */}
                        <div className="tournament-search-container">
                            <FeatherIcon icon="search" size={16} className="tournament-search-icon" />
                            <CreatableSelect
                                autoFocus
                                isMulti
                                options={searchOptions}
                                value={searchChips}
                                inputValue={searchInputValue}
                                onInputChange={(val) => setSearchInputValue(val)}
                                onChange={setSearchChips}
                                // Deferred one tick, same reasoning as
                                // ScheduleTable's own onBlur - a menu-option
                                // click blurs the control a beat before the
                                // click itself registers, so closing
                                // synchronously here would unmount the menu
                                // mid-click and drop the pick.
                                onBlur={() => setTimeout(() => {
                                    if (searchChips.length === 0 && !searchInputValue.trim()) setSearchOpen(false);
                                }, 150)}
                                placeholder={t("Search player by first name, last name")}
                                formatCreateLabel={(val) => `${t("Search text")}: "${val}"`}
                                classNamePrefix="react-select"
                                noOptionsMessage={() => t("No matching options")}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                onCreateOption={(v) => {
                                    setSearchChips(prev => [...prev, { value: `q_${v}`, label: v, type: 'q' }]);
                                    setSearchInputValue("");
                                }}
                            />
                        </div>
                    </li>
                </ul>
            )}
            {/* display:none, not unmounted - see hasUsedPreview/showPreviewFrame's
                own comment above for why this stays alive underneath the
                preview iframe instead of a real unmount/remount. Wrapped in
                PreviewHiddenProvider, same reasoning as App.js's own past
                copy of this wrapper (git history): display:none has no
                effect on anything portaled straight to document.body
                (PageLoader/CustomModal/BottomSheetPanel, all used somewhere
                inside this content tree) - those check this context
                themselves and render nothing while it's true, instead of
                floating visibly over the preview on top of the iframe's own
                copy of the same loader/modal. */}
            <div style={{ display: showPreviewFrame ? "none" : "block" }}>
                <PreviewHiddenProvider value={showPreviewFrame}>
                    <TournamentSwipeTabs tabs={visibleTabs} activeTab={activeTab} disabled={is_authorized}>
                        {/* Same fixed min-height as ClubProfileModal.js's own tab-content
                            wrapper, and for the same reason: a short tab (e.g. Рейтинг
                            with nothing in it yet) would otherwise have too little
                            scrollHeight to hold whatever scroll position the previous
                            tab was already at, and the browser would clamp window.scrollY
                            back down on switching to it alone - this just makes every
                            tab tall enough to hold it, on top of the capture/reassert
                            pair above that actively restores it either way.
                            See the tab-switch effect's own NOTE for why a tried
                            "release this once scroll looks stable" version of this got
                            reverted (caused a visible jump instead of fixing anything). */}
                        <div style={{ minHeight: "100vh" }}>
                            {renderTabContent()}
                        </div>
                        {/* bottom-sheet-page-spacer (was rendered here unconditionally on
                            every tab) is gone - it dates from when BottomSheetPanel stayed
                            permanently mounted as a small always-visible peek strip, which
                            needed every page using it to permanently reserve that same
                            height below their own content (see BottomSheetPanel.jsx's own
                            comment on why that peek was dropped: "a strip that mostly just
                            sat there unused"). The panel now only renders at all while
                            actually open (isOpen is a real controlled prop) and is a
                            position:fixed overlay even then - it was never actually
                            clearing anything, just leaving ~52px of dead white space above
                            the bottom nav on every tab of every tournament. */}
                    </TournamentSwipeTabs>
                </PreviewHiddenProvider>
            </div>
            {hasUsedPreview && (
                <div style={{ display: showPreviewFrame ? "block" : "none" }}>
                    <TournamentPreviewFrame previewWidth={lastPreviewWidth} />
                </div>
            )}
        </Container>
    );
};

export default Tournament;
